/**
 * Chapitres et leçons du programme.
 * Lectures sur les routes utilisateur, suppressions sur ADMIN_URLS.content.
 *
 * Extrait de l'ancien services/api.ts (Lot 8, déplacement pur).
 */

import {
  Lecon,
  Chapitre,
} from '../types';
import type { ApiResponse, DataResponse } from '../types/api';
import { ADMIN_URLS } from '../config/adminApi';
import { createApiClient, HTTP_TIMEOUT, presentParams, withEnv } from '../lib/http';
import { AppEnvironment } from '../config/environment';
import type { BackfillDurationsResult } from './bookContentApi';

const api = createApiClient();

export class ChapitresLeconsApiService {
  /** Liste les leçons de l'environnement. */
  static async getLecons(env: AppEnvironment): Promise<Lecon[]> {
    const response = await api.get<DataResponse<Lecon[]>>(`/lecons`, withEnv(env));
    return response.data.data;
  }

  /** Lit une leçon par son identifiant. */
  static async getLeconById(env: AppEnvironment, id: string): Promise<Lecon> {
    const response = await api.get<DataResponse<Lecon>>(`/lecons/${id}`, withEnv(env));
    return response.data.data;
  }

  /** Met à jour une leçon. */
  static async updateLecon(env: AppEnvironment, id: string, lecon: Partial<Lecon>): Promise<ApiResponse<Lecon>> {
    const response = await api.put<ApiResponse<Lecon>>(`/lecons/${id}`, lecon, withEnv(env));
    return response.data;
  }

  /**
   * Pose la durée réelle (videoDuration, secondes) des leçons depuis la durée
   * mesurée de leur vidéo. ids vide → toutes les leçons de l'env.
   */
  static async backfillLeconDurations(
    env: AppEnvironment,
    ids: string[],
    overwrite: boolean = false
  ): Promise<BackfillDurationsResult> {
    const response = await api.post<BackfillDurationsResult>(
      `${ADMIN_URLS.videoProjects}/lessons/backfill-durations`,
      { ids, overwrite },
      withEnv(env, { timeout: HTTP_TIMEOUT.BATCH })
    );
    return response.data;
  }

  /** Supprime une leçon. */
  static async deleteLecon(env: AppEnvironment, id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.content}/lecons/${id}`, withEnv(env));
    return response.data;
  }

  /** Recherche les leçons de l'environnement selon des filtres. */
  static async getLeconsByFilters(
    env: AppEnvironment,
    filters: { chapitreId?: string } = {}
  ): Promise<Lecon[]> {
    const params = presentParams({ chapitreId: filters.chapitreId });
    const response = await api.get<DataResponse<Lecon[]>>('/lecons', withEnv(env, { params }));
    return response.data.data;
  }

  /** Liste les chapitres de l'environnement. */
  static async getChapitres(env: AppEnvironment): Promise<Chapitre[]> {
    const response = await api.get<DataResponse<Chapitre[]>>(`/chapitres`, withEnv(env));
    return response.data.data;
  }

  /** Recherche les chapitres de l'environnement selon des filtres. */
  static async getChapitresByFilters(
    env: AppEnvironment,
    filters: { matiereId?: string; classeId?: string } = {}
  ): Promise<Chapitre[]> {
    const params = presentParams({ matiereId: filters.matiereId, classeId: filters.classeId });
    const response = await api.get<DataResponse<Chapitre[]>>('/chapitres', withEnv(env, { params }));
    return response.data.data;
  }

  /** Lit un chapitre par son identifiant. */
  static async getChapitreById(env: AppEnvironment, id: string): Promise<Chapitre> {
    const response = await api.get<DataResponse<Chapitre>>(`/chapitres/${id}`, withEnv(env));
    return response.data.data;
  }

  /** Met à jour un chapitre. */
  static async updateChapitre(env: AppEnvironment, id: string, chapitre: Partial<Chapitre>): Promise<ApiResponse<Chapitre>> {
    const response = await api.put<ApiResponse<Chapitre>>(`/chapitres/${id}`, chapitre, withEnv(env));
    return response.data;
  }

  /** Supprime un chapitre. */
  static async deleteChapitre(env: AppEnvironment, id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.content}/chapitres/${id}`, withEnv(env));
    return response.data;
  }
}

export default ChapitresLeconsApiService;
